/**
 * Web twin of the native "Usage & credits" panel (`UsagePanel.tsx`), used by
 * the settings dialog pane and the `/settings/usage` route. DOM + the shared
 * `tabbio-sub-*` / `tabbio-usage-*` classes in `global.css`, so light/dark
 * follow `[data-theme]` and RTL follows `dir` through logical properties.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useLocale } from '../../../hooks/useLocale';
import { useTheme } from '../../../hooks/useTheme';
import { useSubscriptionState } from '../../../hooks/useSubscriptionState';
import { useUpgradeModal } from '../../../providers/useUpgradeModal';
import { useWorkspace } from '../../../providers/useWorkspace';
import {
  buildSubscriptionCreditUsageLabels,
  selectSubscriptionCreditUsageDisplay,
} from '../../../lib/subscription-credit-usage';
import {
  barFillPercent,
  displayPercentRemaining,
  formatCreditsValue,
  formatPercentRemaining,
  formatResetLabel,
  selectCreditsFootnote,
  selectCreditsRemaining,
  selectUsageLimitRows,
} from '../../../lib/usage-limits';
import { MaterialSymbol } from '../../icons/MaterialSymbol';
import { SubButton } from '../../subscription/journey-ui.web';

export function UsagePanel() {
  const { t, i18n } = useTranslation('common');
  const locale = i18n.resolvedLanguage ?? i18n.language;
  const { isRTL } = useLocale();
  const { theme } = useTheme();
  const upgradeModal = useUpgradeModal();
  const { catalog, snapshot, isLoading, error, refetchSubscription } = useSubscriptionState();
  const { mode } = useWorkspace();
  const [infoOpen, setInfoOpen] = useState(false);
  const infoRef = useRef<HTMLDivElement>(null);
  const bubbleId = useId();
  const display = useMemo(
    () =>
      selectSubscriptionCreditUsageDisplay({
        catalog,
        snapshot,
        isLoading,
        workspaceMode: mode,
        labels: buildSubscriptionCreditUsageLabels(t),
      }),
    [catalog, isLoading, mode, snapshot, t],
  );

  // Toggletip: closes on a click outside it or on Escape.
  useEffect(() => {
    if (!infoOpen) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!infoRef.current?.contains(event.target as Node)) setInfoOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setInfoOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [infoOpen]);

  if (!snapshot) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} className="tabbio-sub tabbio-usage-state">
        {isLoading ? (
          <ActivityIndicator
            color={theme.textSecondary}
            accessibilityLabel={t('subscription.states.loadingLabel')}
          />
        ) : (
          <>
            <p className="tabbio-usage-state-title">{t('subscription.states.error.title')}</p>
            <p className="tabbio-sub-fine" style={{ textAlign: 'center', marginTop: 0 }}>
              {t(error ? 'subscription.states.error.body' : 'subscription.states.offline.body')}
            </p>
            <SubButton
              kind="secondary"
              icon={<MaterialSymbol name="refresh" size={20} />}
              onClick={() => void refetchSubscription()}
            >
              {t('subscription.states.tryAgain')}
            </SubButton>
          </>
        )}
      </div>
    );
  }

  if (!display.hasCreditUsage) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} className="tabbio-sub">
        <p className="tabbio-usage-footnote">{display.unavailableLabel}</p>
      </div>
    );
  }

  const rows = selectUsageLimitRows(display);
  const creditsValue = formatCreditsValue(selectCreditsRemaining(display), locale, t);
  const footnote = selectCreditsFootnote(display.tier);

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="tabbio-sub tabbio-usage">
      <div className="tabbio-usage-label-row is-first" ref={infoRef}>
        <h2 className="tabbio-usage-label">{t('settings.usageScreen.limitSection')}</h2>
        <button
          type="button"
          className="tabbio-usage-info-btn"
          aria-label={t('settings.usageScreen.limitInfoLabel')}
          aria-expanded={infoOpen}
          aria-controls={bubbleId}
          onClick={() => setInfoOpen((open) => !open)}
        >
          <MaterialSymbol name="info" size={18} />
        </button>
        <div
          id={bubbleId}
          role="status"
          className="tabbio-usage-bubble"
          hidden={!infoOpen}
        >
          {infoOpen ? t('settings.usageScreen.limitInfo') : null}
        </div>
      </div>

      <div className="tabbio-sub-group">
        {rows.map((row) => {
          const title = t(row.labelKey);
          const value = row.unlimited
            ? t('settings.usageScreen.unlimited')
            : formatPercentRemaining(row.percentRemaining, locale, t);
          const caption = row.unlimited
            ? null
            : formatResetLabel(row.resetAt, row.kind, locale, t);
          return (
            <div key={row.kind} className="tabbio-usage-row">
              <div className="tabbio-usage-row-line">
                <span className="tabbio-usage-row-title">{title}</span>
                {value ? <span className="tabbio-usage-row-value">{value}</span> : null}
              </div>
              {row.unlimited ? null : (
                <div
                  className="tabbio-usage-track"
                  role="progressbar"
                  aria-label={title}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={displayPercentRemaining(row.percentRemaining) ?? 0}
                  aria-valuetext={value ?? undefined}
                >
                  <div
                    className="tabbio-usage-fill"
                    style={{ width: `${barFillPercent(row.percentRemaining)}%` }}
                  />
                </div>
              )}
              {caption ? <p className="tabbio-usage-caption">{caption}</p> : null}
            </div>
          );
        })}
      </div>

      <div className="tabbio-usage-label-row">
        <h2 className="tabbio-usage-label">{t('settings.usageScreen.creditsSection')}</h2>
      </div>
      <div className="tabbio-sub-group">
        <div className="tabbio-usage-row">
          <div className="tabbio-usage-row-line">
            <span className="tabbio-usage-row-title">
              {t('settings.usageScreen.creditsRemaining')}
            </span>
            <span className="tabbio-usage-row-value">{creditsValue}</span>
          </div>
        </div>
      </div>
      <p className="tabbio-usage-footnote">{t(footnote.footnoteKey)}</p>
      {footnote.upgrade ? (
        <button
          type="button"
          className="tabbio-sub-link tabbio-usage-upgrade"
          onClick={() => upgradeModal.open('individual', { tier: footnote.upgrade!.tier })}
        >
          {t(footnote.upgrade.labelKey)}
        </button>
      ) : null}
    </div>
  );
}
